import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import Field from '@/components/common/Field.jsx';
import EmptyState from '@/components/common/EmptyState.jsx';
import {
  cerrarRecepcion,
  crearRecepcion,
  guiasPendientesDeRecepcion,
  marcarGuiaConIncidencia,
  marcarGuiaFaltante,
  marcarGuiaRecibida,
  recibirTodoPorFoja,
} from '@/services/recepcionCarga.js';
import {
  CargasPage,
  CargasSection,
  CargasStickyActions,
  CargasTableShell,
  CargasViewport,
  cargasButtonClassName,
  cargasInputClassName,
  cargasPrimaryButtonClassName,
  useSaveShortcut,
} from '../components/CargasLayout.jsx';

const RESULT_LABELS = { recibida: 'Recibida ✓', con_incidencia: 'Con incidencia ✓', faltante: 'Faltante ✓' };

export default function RecepcionWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { sucursalActiva, sucursal, user } = useAuth();
  // The reception always runs in the operator's active branch (the API rejects other branches).
  const sucursalId = Number(sucursalActiva?.id || sucursal?.id || user?.sucursal_activa_id || user?.sucursal_id || 0);
  const sucursalLabel = (sucursalActiva || sucursal)?.nombre || (sucursalId ? `Sucursal ${sucursalId}` : 'Sin sucursal activa');
  const [step, setStep] = useState(1);
  const [tipo, setTipo] = useState(searchParams.get('tipo') || 'foja');
  const [modo, setModo] = useState('manual');
  const [fojaId, setFojaId] = useState('');
  const [recepcion, setRecepcion] = useState(null);
  const [pendientes, setPendientes] = useState([]);
  const [resultados, setResultados] = useState({});
  const [busyGuia, setBusyGuia] = useState(null);
  const [error, setError] = useState(null);

  const fojaFiltro = tipo === 'foja' && fojaId ? fojaId : undefined;

  const loadPendientes = useCallback(() => {
    if (!sucursalId) return Promise.resolve();
    return guiasPendientesDeRecepcion({ sucursalId, ...(fojaFiltro ? { fojaId: fojaFiltro } : {}) })
      .then(setPendientes)
      .catch(err => {
        setPendientes([]);
        setError(err?.message || 'No se pudieron cargar las guías pendientes.');
      });
  }, [fojaFiltro, sucursalId]);

  useEffect(() => {
    if (step >= 2) void loadPendientes();
  }, [loadPendientes, step]);

  const selectedGuias = useMemo(() => pendientes.slice(0, 200), [pendientes]);
  const contadores = useMemo(() => {
    const values = Object.values(resultados);
    return {
      pendientes: selectedGuias.filter(g => !resultados[g.id]).length,
      recibidas: values.filter(value => value === 'recibida').length,
      incidencias: values.filter(value => value === 'con_incidencia').length,
      faltantes: values.filter(value => value === 'faltante').length,
    };
  }, [resultados, selectedGuias]);

  const onCreate = async () => {
    setError(null);
    try {
      const created = await crearRecepcion({ sucursalId, tipo, fojaId: fojaId || null, modo });
      setRecepcion(created);
      setResultados({});
      setStep(3);
    } catch (err) {
      setError(err?.message || 'No se pudo crear la recepción.');
    }
  };

  const handlePrimary = async () => {
    if (step < 2) {
      setStep(2);
      return;
    }

    if (step === 2) {
      await onCreate();
      return;
    }

    if (step === 3 && recepcion) {
      try {
        await cerrarRecepcion(recepcion.id);
        navigate('/cargas/recepcion');
      } catch (err) {
        setError(err?.message || 'No se pudo cerrar la recepción.');
      }
    }
  };

  /** Each guía is recorded once; failures are shown and the row stays available to retry. */
  const registrar = async (guia, estado) => {
    if (!recepcion || busyGuia) return;
    let detalle;
    if (estado !== 'recibida') {
      detalle = window.prompt(
        estado === 'faltante' ? `¿Qué falta de la guía ${guia.numero}?` : `Describí la incidencia de la guía ${guia.numero}:`,
        estado === 'faltante' ? 'No llegó el bulto' : '',
      );
      if (detalle == null) return;
      detalle = detalle.trim() || (estado === 'faltante' ? 'No llegó el bulto' : 'Incidencia informada');
    }
    setBusyGuia(guia.id);
    setError(null);
    try {
      if (estado === 'recibida') await marcarGuiaRecibida(recepcion.id, guia.id);
      else if (estado === 'faltante') await marcarGuiaFaltante(recepcion.id, guia.id, detalle);
      else {
        await marcarGuiaConIncidencia({ recepcionId: recepcion.id, guiaId: guia.id, incidenciaTipo: 'otros', incidenciaDetalle: detalle });
      }
      setResultados(prev => ({ ...prev, [guia.id]: estado }));
    } catch (err) {
      setError(`${guia.numero}: ${err?.message || 'No se pudo registrar la guía.'}`);
    } finally {
      setBusyGuia(null);
    }
  };

  const recibirTodo = async () => {
    if (!recepcion) return;
    setError(null);
    try {
      await recibirTodoPorFoja(recepcion.id);
      setResultados(Object.fromEntries(selectedGuias.map(g => [g.id, 'recibida'])));
      await loadPendientes();
    } catch (err) {
      setError(err?.message || 'No se pudo recibir la foja completa.');
    }
  };

  useSaveShortcut(handlePrimary);

  return (
    <CargasPage
      title="Nueva recepcion"
      description="Configura la recepcion y confirma la carga recibida sin perder visibilidad de las acciones."
      hideHeaderOnCompact
      footer={
        <CargasStickyActions
          summary={
            <div className="flex flex-wrap items-center gap-3 text-xs text-[rgba(var(--muted),0.92)]">
              <span>Paso {step} de 3</span>
              <span>Guias detectadas: {selectedGuias.length}</span>
              <span>Modo: {modo}</span>
              {step === 3 ? (
                <span>
                  Pendientes: {contadores.pendientes} · Recibidas: {contadores.recibidas} · Con incidencia: {contadores.incidencias} · Faltantes: {contadores.faltantes}
                </span>
              ) : null}
            </div>
          }
          actions={
            <>
              <button type="button" className={cargasButtonClassName} onClick={() => navigate('/cargas/recepcion')}>
                Cerrar
              </button>
              {step === 3 && tipo === 'foja' && recepcion ? (
                <button
                  type="button"
                  className={cargasButtonClassName}
                  onClick={recibirTodo}
                >
                  Marcar todo recibido
                </button>
              ) : null}
              <button type="button" className={cargasPrimaryButtonClassName} onClick={handlePrimary}>
                {step < 2 ? 'Continuar' : step === 2 ? 'Guardar' : 'Cerrar recepcion'}
              </button>
            </>
          }
        />
      }
    >
      <CargasViewport className="grid grid-cols-12 gap-3">
        <CargasSection
          title="Datos operativos"
          className="col-span-12 md:col-span-8"
          bodyClassName="grid grid-cols-12 gap-2 p-4"
        >
          <div className="col-span-12 md:col-span-4">
            <Field label="Sucursal" htmlFor="sucursal" required>
              <input id="sucursal" value={sucursalLabel} readOnly className={cargasInputClassName} />
            </Field>
          </div>
          <div className="col-span-12 md:col-span-4">
            <Field label="Tipo" htmlFor="tipo" required>
              <select id="tipo" value={tipo} onChange={e => setTipo(e.target.value)} className={cargasInputClassName}>
                <option value="foja">Foja</option>
                <option value="guia">Guia</option>
              </select>
            </Field>
          </div>
          <div className="col-span-12 md:col-span-4">
            <Field label="Modo" htmlFor="modo" required>
              <select id="modo" value={modo} onChange={e => setModo(e.target.value)} className={cargasInputClassName}>
                <option value="manual">Manual</option>
                <option value="qr">QR</option>
              </select>
            </Field>
          </div>
          {tipo === 'foja' ? (
            <div className="col-span-12 md:col-span-4">
              <Field label="Foja" htmlFor="foja">
                <input
                  id="foja"
                  value={fojaId}
                  onChange={e => setFojaId(e.target.value)}
                  className={cargasInputClassName}
                />
              </Field>
            </div>
          ) : null}

          {step === 2 ? (
            <div className="col-span-12 rounded-xl border border-[rgba(var(--border),0.45)] px-3 py-2 text-sm">
              Se usaran las guias pendientes de la sucursal seleccionada para crear la recepcion.
            </div>
          ) : null}

          {error ? (
            <div role="alert" className="col-span-12 rounded-xl border border-red-500/50 bg-red-500/10 px-3 py-2 text-sm">
              {error}
            </div>
          ) : null}

          {step === 3 ? (
            <div className="col-span-12 min-h-0">
              {selectedGuias.length === 0 ? (
                <EmptyState title="Sin guias pendientes" description="No hay guias para procesar en esta recepcion." />
              ) : (
                <CargasTableShell className="max-h-[300px]">
                  <table className="min-w-full text-left text-sm">
                    <thead className="sticky top-0 bg-[rgb(var(--panel))]">
                      <tr>
                        <th className="px-3 py-2">Guia</th>
                        <th className="px-3 py-2">Estado</th>
                        <th className="px-3 py-2">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedGuias.map(g => (
                        <tr key={g.id} className="border-t border-[rgba(var(--border),0.45)]">
                          <td className="px-3 py-2">{g.numero}</td>
                          <td className="px-3 py-2">{g.estadoLogistico}</td>
                          <td className="px-3 py-2">
                            {resultados[g.id] ? (
                              <span className="font-semibold">{RESULT_LABELS[resultados[g.id]]}</span>
                            ) : (
                              <div className="flex flex-wrap gap-2">
                                {[
                                  ['recibida', 'Recibida'],
                                  ['con_incidencia', 'Con incidencia'],
                                  ['faltante', 'Faltante'],
                                ].map(([estado, label]) => (
                                  <button
                                    key={estado}
                                    type="button"
                                    className={cargasButtonClassName}
                                    disabled={Boolean(busyGuia)}
                                    onClick={() => registrar(g, estado)}
                                  >
                                    {label}
                                  </button>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CargasTableShell>
              )}
            </div>
          ) : null}
        </CargasSection>

        <CargasSection
          title="Resumen"
          className="col-span-12 md:col-span-4"
          bodyClassName="space-y-2 p-4 text-sm"
        >
          <div className="rounded-xl border border-[rgba(var(--border),0.45)] px-3 py-2">Paso actual: {step}</div>
          <div className="rounded-xl border border-[rgba(var(--border),0.45)] px-3 py-2">Tipo: {tipo}</div>
          <div className="rounded-xl border border-[rgba(var(--border),0.45)] px-3 py-2">Modo: {modo}</div>
          <div className="rounded-xl border border-[rgba(var(--border),0.45)] px-3 py-2">
            Guias detectadas: {selectedGuias.length}
          </div>
          {recepcion ? (
            <div className="rounded-xl border border-[rgba(var(--border),0.45)] px-3 py-2">
              Recepcion #{recepcion.id}
            </div>
          ) : null}
          <p className="text-xs text-[rgba(var(--muted),0.92)]">Atajo disponible: `Ctrl + S`.</p>
        </CargasSection>
      </CargasViewport>
    </CargasPage>
  );
}
